import React from 'react';

/**
 * DeferredBackground
 * - Defers applying `backgroundImage` until the wrapper is near the viewport
 * - Optional `delayMs` to delay even after it becomes visible
 */
export default function DeferredBackground({
  as: Tag = 'div',
  bgImage,
  delayMs = 0,
  rootMargin = '300px',
  threshold = 0.01,
  style,
  ...rest
}) {
  const ref = React.useRef(null);
  const [shouldLoad, setShouldLoad] = React.useState(false);

  React.useEffect(() => {
    if (!bgImage) return;

    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setShouldLoad(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    let timeoutId;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting) return;

        observer.disconnect();
        if (delayMs > 0) {
          timeoutId = window.setTimeout(() => setShouldLoad(true), delayMs);
        } else {
          setShouldLoad(true);
        }
      },
      { rootMargin, threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
      if (timeoutId) window.clearTimeout(timeoutId);
    };
  }, [bgImage, delayMs, rootMargin, threshold]);

  return (
    <Tag
      ref={ref}
      style={{
        ...style,
        ...(shouldLoad && bgImage ? { backgroundImage: `url(${bgImage})` } : {}),
      }}
      data-bgimage={bgImage}
      {...rest}
    />
  );
}

